import { Avatar, Button, Dropdown, Space } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackIcon from "../../../assets/icons/BackIcon";
import { IMAGE_URL } from "../../../constants/constants";
import { disableTodayAndFutureDates } from "../../../lib/time";
import {
  singDiscussionBoardAsync,
  updateDiscussionBoardAsync,
} from "../../../store/features/discussionBoard/discussionBoardApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";

export default function PendingRequestViewDetails() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { singleDiscussionBoardData } = useAppSelector(
    (state) => state.discussionBoard
  );
  const [localComments, setLocalComments] = useState<Comment[]>([]);

  // Fetch single discussion board data
  useEffect(() => {
    if (id) {
      dispatch(
        singDiscussionBoardAsync({
          params: {
            id: id,
          },
        })
      );
    }
  }, [dispatch, id]);

  const handleUpdateStatus = (status: string) => {
    if (id) {
      dispatch(
        updateDiscussionBoardAsync({
          data: {
            discussionBoardStatus: status,
          },
          others: {
            id: id,
            reRender: () => {
              // Refresh the data after status update
              dispatch(
                singDiscussionBoardAsync({
                  params: {
                    id: id,
                  },
                })
              );
            },
          },
        })
      );
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-3 text-[#5E718D] items-center">
          <div className="cursor-pointer" onClick={() => navigate(-1)}>
            <BackIcon />
          </div>
          <div className="text-2xl">Topic Details</div>
        </div>

        {/* Status Update Buttons moved to top right */}
        <div className="flex items-center gap-4">
          <Button
            type="primary"
            className={`${
              singleDiscussionBoardData?.discussionBoardStatus === "Published"
                ? "bg-green-500"
                : "bg-gray-500"
            } text-white`}
            onClick={() => handleUpdateStatus("Published")}
          >
            Publish
          </Button>
          <Button
            type="default"
            className={`${
              singleDiscussionBoardData?.discussionBoardStatus === "Draft"
                ? "bg-yellow-500"
                : "bg-gray-500"
            } text-white`}
            onClick={() => handleUpdateStatus("Draft")}
          >
            Draft
          </Button>
          <Button
            type="default"
            className={`${
              singleDiscussionBoardData?.discussionBoardStatus === "Archived"
                ? "bg-red-500"
                : "bg-gray-500"
            } text-white`}
            onClick={() => handleUpdateStatus("Archived")}
          >
            Archive
          </Button>
          <Button
            type="default"
            className={`${
              singleDiscussionBoardData?.discussionBoardStatus === "Pending"
                ? "bg-blue-500"
                : "bg-gray-500"
            } text-white`}
            onClick={() => handleUpdateStatus("Pending")}
          >
            Pending
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-white p-4">
        {/* Post Title and Meta */}
        <div className="mb-1">
          <h2 className="text-xl font-semibold text-[#343A40] mb-1">
            {singleDiscussionBoardData?.discussionBoardTitle || "title"}
          </h2>
          <div className="flex gap-2 items-center mb-4">
            <span className="w-3 h-3 bg-[#EA868F]"></span>
            <span>
              {singleDiscussionBoardData?.categoryId?.categoryName ||
                "category name"}
            </span>
          </div>

          <Dropdown
            dropdownRender={() => (
              <ProfilePopupCard
                profileData={
                  singleDiscussionBoardData?.discussionBoardCreatedBy
                }
              />
            )}
          >
            <div className="text-sm text-[#343A40] flex items-center">
              <Avatar
                src={
                  IMAGE_URL +
                  singleDiscussionBoardData?.discussionBoardCreatedBy
                    ?.profilePicture
                }
                className="mr-2"
              />
              <div className="text-[#495057] flex items-center justify-between w-full">
                <div>
                  <Space className="cursor-pointer">
                    <span>
                      {singleDiscussionBoardData?.discussionBoardCreatedBy
                        ?.fullName || "Name"}
                    </span>
                  </Space>
                </div>
                <span>
                  {disableTodayAndFutureDates(
                    singleDiscussionBoardData?.updatedAt || "date"
                  )}
                </span>
              </div>
            </div>
          </Dropdown>
        </div>

        {/* Post Content */}
        <div className="mb-4 ml-10">
          {/* <p className="text-[#343A40] mb-2">
            {singleDiscussionBoardData?.discussionBoardContent}
          </p> */}
          <div 
            className="text-[#343a40] text-base leading-7"
            dangerouslySetInnerHTML={{ 
              __html: singleDiscussionBoardData?.discussionBoardContent || ""}}
          />
        </div>

        {/* Post Actions */}
        {/* <div className="flex items-center gap-4 ml-10">
          <Button
            type="link"
            className="flex items-center gap-2 p-0 text-[#6C757D]"
          >
            <LinkIcon />
            <span>Link Copy</span>
          </Button>
          <Button
            type="link"
            className="flex items-center gap-2 p-0 text-[#6C757D]"
          >
            <BookmarkIcon />
            <span>Bookmark</span>
          </Button>
        </div> */}

        {/* Current Status Display */}
        <div className="ml-10 mt-4">
          <span className="text-[#495057] mr-2">Current Status:</span>
          <span
            className={`status-${singleDiscussionBoardData?.discussionBoardStatus?.toLowerCase()}`}
          >
            {singleDiscussionBoardData?.discussionBoardStatus || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProfilePopupCard({ profileData }: any) {
  return (
    <div className="flex gap-3 p-4 bg-white shadow-lg rounded-lg w-[345px]">
      <div>
        <Avatar src={IMAGE_URL + profileData?.profilePicture} size="large" />
      </div>
      <div>
        <div className="text-[#212529] text-lg">
          {profileData?.fullName || "Name"}
        </div>
        <div className="text-[#495057] text-sm">
          {profileData?.designation || "Designation"}
        </div>
        <div className="text-sm text-[#6C757D]">
          {profileData?.mobile || "Mobile"}
        </div>
      </div>
    </div>
  );
}
